// src/components/cart/CartItem.js

import React, { useEffect, useState } from 'react';
import { useCart } from '../../context/CartContext';
import { useProducts } from '../../context/ProductContext';
import { useTheme } from '../../components/ThemeProvider';
import Button from '../common/Button';

const formatSLE = (value) =>
  new Intl.NumberFormat('en-SL', {
    style: 'currency',
    currency: 'SLE',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

function RemoveConfirmationModal({
  open,
  itemName,
  onCancel,
  onConfirm,
}) {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-3 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-item-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onCancel();
        }
      }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-[1.75rem] bg-white shadow-2xl">
        <div className="p-6 sm:p-8">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-7 w-7 text-red-600"
              aria-hidden="true"
            >
              <path d="M3 6h18" />
              <path d="M8 6V4h8v2" />
              <path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v5" />
              <path d="M14 11v5" />
            </svg>
          </div>

          <div className="mt-5 text-center">
            <h2
              id="remove-item-title"
              className="text-2xl font-bold text-gray-900"
            >
              Remove item?
            </h2>

            <p className="mt-3 text-sm leading-6 text-gray-600">
              Are you sure you want to remove{' '}
              <strong className="text-gray-900">
                {itemName}
              </strong>{' '}
              from your basket?
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-100 bg-gray-50 p-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="w-full rounded-full border border-gray-300 bg-white px-6 py-3 text-sm font-bold text-gray-700 transition hover:bg-gray-100 sm:w-auto"
          >
            Keep item
          </button>

          <button
            type="button"
            onClick={onConfirm}
            className="w-full rounded-full bg-red-600 px-6 py-3 text-sm font-bold !text-white transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto"
          >
            Remove item
          </button>
        </div>
      </div>
    </div>
  );
}

function CartItem({ item }) {
  const { updateQuantity, removeFromCart } = useCart();
  const { products } = useProducts();
  const { primaryColor } = useTheme();

  const [error, setError] = useState('');
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const isMerchantItem = Boolean(
    item.merchant_id &&
      item.source_product_id !== undefined,
  );

  const product = isMerchantItem
    ? item
    : products.find(
        (candidate) =>
          String(candidate.id) === String(item.id),
      );

  const requestRemoval = () => {
    setShowRemoveModal(true);
  };

  const cancelRemoval = () => {
    setShowRemoveModal(false);
  };

  const confirmRemoval = () => {
    removeFromCart(item.variantKey);
    setShowRemoveModal(false);
  };

  if (
    !product ||
    (!isMerchantItem &&
      ['real_estate', 'cars'].includes(product.category))
  ) {
    return (
      <>
        <div className="rounded-2xl bg-white p-5 text-center">
          <p className="font-montserrat text-base text-red-600">
            Invalid item: {item.name} cannot be added to the
            basket.
          </p>

          <Button
            onClick={requestRemoval}
            className="mt-4 rounded-full bg-red-600 px-5 py-2.5 font-montserrat text-sm font-bold !text-white hover:bg-red-700"
            aria-label={`Remove invalid item ${item.name} from basket`}
          >
            Remove
          </Button>
        </div>

        <RemoveConfirmationModal
          open={showRemoveModal}
          itemName={item.name}
          onCancel={cancelRemoval}
          onConfirm={confirmRemoval}
        />
      </>
    );
  }

  const handleQuantityChange = (delta) => {
    const currentQuantity = Number(item.quantity || 0);
    const newQuantity = currentQuantity + delta;

    if (newQuantity < 1) {
      setError('Quantity cannot be less than 1.');
      return;
    }

    if (
      Number(product.has_stock) === 1 &&
      newQuantity > Number(product.stock || 0)
    ) {
      setError(`Only ${product.stock} items are in stock.`);
      return;
    }

    setError('');
    updateQuantity(item.variantKey, newQuantity);
  };

  const getImageSrc = () => {
    if (item.cart_image) {
      return item.cart_image;
    }

    if (product.image) {
      return `${process.env.REACT_APP_PRODUCT_URL || ''}${
        product.image
      }`;
    }

    return '/assets/images/placeholder.jpg';
  };

  const productPrice = Number(item.price || 0);
  const quantity = Number(item.quantity || 0);
  const lineTotal = productPrice * quantity;

  const isStockTracked =
    Number(product.has_stock) === 1;

  const isAtStockLimit =
    isStockTracked &&
    quantity >= Number(product.stock || 0);

  return (
    <>
      <article className="overflow-hidden bg-white">
        <div className="flex items-start gap-3 p-3 sm:items-center sm:gap-5 sm:p-5">
          <img
            src={getImageSrc()}
            alt={`${item.name} ${
              item.selectedColor || ''
            }`.trim()}
            className="h-24 w-24 shrink-0 rounded-xl object-cover sm:h-28 sm:w-28"
            onError={(event) => {
              event.currentTarget.src =
                '/assets/images/placeholder.jpg';
            }}
          />

          <div className="min-w-0 flex-1">
            <h2 className="line-clamp-2 break-words font-playfair-display text-lg font-semibold leading-tight text-primary sm:text-xl">
              {item.name}
            </h2>

            <p className="mt-1 font-montserrat text-xs text-secondary sm:text-sm">
              {formatSLE(productPrice)} each
            </p>

            <p className="mt-1 font-montserrat text-base font-bold text-primary sm:text-lg">
              {formatSLE(lineTotal)}
            </p>

            {item.selectedSize && (
              <p className="mt-1 text-xs text-gray-500">
                Size: {item.selectedSize}
              </p>
            )}

            {item.selectedColor && (
              <p className="mt-1 text-xs text-gray-500">
                Colour: {item.selectedColor}
              </p>
            )}

            {isStockTracked && (
              <p className="mt-1 font-montserrat text-xs">
                {Number(product.stock) > 0 ? (
                  <span className="text-green-600">
                    In stock ({product.stock})
                  </span>
                ) : (
                  <span className="text-red-600">
                    Out of stock
                  </span>
                )}
              </p>
            )}
          </div>
        </div>

        {error && (
          <div className="mx-3 mb-3 rounded-xl bg-red-50 px-4 py-3 sm:mx-5">
            <p className="font-montserrat text-xs font-medium text-red-600">
              {error}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-3 py-3 sm:px-5">
          <div>
            <span className="mb-1 block font-montserrat text-[11px] font-medium text-secondary">
              Quantity
            </span>

            <div className="flex items-center rounded-full border border-primary/10 bg-cream/60 p-1">
              <Button
                type="button"
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
                className="flex h-9 w-9 items-center justify-center rounded-full p-0 text-lg disabled:cursor-not-allowed disabled:opacity-40"
                aria-label={`Decrease quantity of ${item.name}`}
                style={{ '--primary': primaryColor }}
              >
                −
              </Button>

              <span className="min-w-9 text-center font-montserrat text-sm font-bold text-secondary">
                {quantity}
              </span>

              <Button
                type="button"
                onClick={() => handleQuantityChange(1)}
                disabled={isAtStockLimit}
                className="flex h-9 w-9 items-center justify-center rounded-full p-0 text-lg disabled:cursor-not-allowed disabled:opacity-40"
                aria-label={`Increase quantity of ${item.name}`}
                style={{ '--primary': primaryColor }}
              >
                +
              </Button>
            </div>
          </div>

          <Button
            type="button"
            onClick={requestRemoval}
            className="shrink-0 rounded-full bg-red-600 px-4 py-2.5 font-montserrat text-xs font-bold !text-white hover:bg-red-700 sm:px-5 sm:text-sm"
            aria-label={`Remove ${item.name} from basket`}
          >
            Remove
          </Button>
        </div>

        {!isStockTracked && quantity >= 10 && (
          <p className="px-3 pb-3 text-xs text-gray-500 sm:px-5">
            No stock limit—order as many as needed.
          </p>
        )}
      </article>

      <RemoveConfirmationModal
        open={showRemoveModal}
        itemName={item.name}
        onCancel={cancelRemoval}
        onConfirm={confirmRemoval}
      />
    </>
  );
}

export default CartItem;